import { createMboClients, saveErrorDetails, updateProfileFkoApi, verifyRecaptcha } from '@/api/api';
import { usePrefetchPrograms } from '@/api/Classes/useGetPrograms';
import { useGetCachedUserExists } from '@/api/FKO/useGetCachedUserExists';
import { logAnalyticsEvent } from '@/common/analytics-events';
import { getFkoInitialParamsLocalstorage, setFkoInitialParamsLocalstorage } from '@/common/fko-localstorage';
import { IS_TEST_ENVIRONMENT } from '@/common/isTestEnvironment';
import { useLoadScript } from '@/common/use-load-script.hook';
import { useGetSport } from '@/common/useGetSport';
import { EmailOtpModal } from '@/components/AccountVerification/EmailOtpModal';
import { PhoneVerificationModal } from '@/components/AccountVerification/PhoneVerificationModal';
import { VerificationChoiceModal } from '@/components/AccountVerification/VerificationChoiceModal';
import { Button } from '@/components/Connects/Button';
import { PageHeading } from '@/components/Connects/PageHeading';
import Spinner from '@/components/Loader/Spinner';
import { MenuBar } from '@/components/MenuBar/MenuBar';
import { getLocationBySiteId } from '@/constants/locations';
import { isLocationPermitted } from '@/feature-flags/fko-locations';
import { useAccountVerification, VerificationPayload } from '@/hooks/useAccountVerification';
import { useFormSubmissionIpAddress } from '@/hooks/useFormSubmissionIpAddress';
import { FkoRegistrationLandingForm, RegistrationFormTypes } from '@/pages/RegistrationLanding/RegistrationLandingForm';
import { useAppDispatch } from '@/redux/reduxUtils';
import { FkoUserData, setFkoInitialParams, setFkoPlayersArray, setFkoUserData } from '@/redux/slices/fkoFormData';
import store from '@/redux/store';
import { SanitizedProfile } from '@/user/player-info.interface';
import { setCurrentAccountHolderProfileId } from '@/user/user-utils';
import { executeRecaptchaV3 } from '@/utils/utils';
import { generateUuid } from '@/utils/uuid';
import { memo, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { parseFkoParamsFromSearchParams, recaptchaSiteKey, sendFkoHubspotFormData } from '../../common/fko-utils';
import { fullStoryService } from '../../integrations/fullstory.service';

export const ProgramsPromotionalLanding = memo(() => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {sessionId, siteId} = useParams();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const {getCachedUserExists, getCachedUserExistsStatus} = useGetCachedUserExists();
  const ipAddress = useFormSubmissionIpAddress();
  const {basePathWithoutSessionId, sport, fkoMethod, fkoAnalyticsPrefix, isClasses, isFreeTrialClasses} = useGetSport();

  useLoadScript(`https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`);
  usePrefetchPrograms({siteId, select: (programs) => programs.filter((p) => p.classSchedules?.length)});

  useEffect(() => {
    logAnalyticsEvent(`classes_form_start`);
    logAnalyticsEvent(`${fkoAnalyticsPrefix}_landing_view`); // TODO remove, probably
    const urlParams = parseFkoParamsFromSearchParams(new URL(document.location.href).searchParams);
    // if we have a valid sessionId, we reload the correct initial params (this is probably a page refresh)
    if (sessionId) {
      const _classesParams = getFkoInitialParamsLocalstorage({paramsId: sessionId, sport}) ?? {...urlParams, sport};
      dispatch(setFkoInitialParams(_classesParams));
    } else {
      const classesParams = {...urlParams, sport};
      const {paramsId} = setFkoInitialParamsLocalstorage(classesParams);
      dispatch(setFkoInitialParams(classesParams));
      navigate(`/${paramsId}${basePathWithoutSessionId}`, {replace: true});
    }
    // ---don't include `sessionId` in dependencies---
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePathWithoutSessionId, dispatch, fkoAnalyticsPrefix, navigate, sport]);

  // this is used to check if a user exists and create an initial profile if they don't
  const processUserExistsOrCreateInitialProfile = useCallback(
    async (payload: VerificationPayload) => {
      const {lookupResult, email, siteId, formData} = payload;
      const profiles = lookupResult?.data?.profiles ?? [];
      let accountHolder: SanitizedProfile | null =
        profiles.find((profile) => profile.accountHolder) ??
        profiles.find(
          (profile) =>
            profile.firstName.toLowerCase() === formData?.firstName.toLowerCase() &&
            profile.lastName.toLowerCase() === formData?.lastName.toLowerCase()
        ) ??
        (profiles.length ? profiles[0] : null);

      const _userData: FkoUserData = {
        dob: accountHolder?.dob ?? formData.dob,
        email,
        firstName: accountHolder?.firstName ?? formData.firstName.trim(),
        lastName: accountHolder?.lastName ?? formData.lastName.trim(),
        location: accountHolder?.mboDetails?.siteId ?? siteId,
        phoneNumber:
          accountHolder?.phoneNumber && accountHolder?.phoneNumber !== '0000000000'
            ? accountHolder?.phoneNumber
            : formData?.phoneNumber,
      };
      dispatch(setFkoUserData(_userData));

      if (profiles.length) {
        dispatch(setFkoPlayersArray(profiles));
      } else {
        const newProfile = await updateProfileFkoApi({
          email,
          siteId,
          update: {
            dob: formData.dob,
            email,
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            phoneNumber: formData.phoneNumber,
            tracking: {
              fko: store.getState().fkoFormDataReducer.initialParams || {},
            },
          },
        });
        accountHolder = newProfile;

        try {
          const res = await createMboClients({
            siteId: formData.location,
            email,
            clients: [
              {
                accountHolder: true,
                firstName: newProfile.firstName,
                lastName: newProfile.lastName,
                birthdate: newProfile.dob || '2000-01-01',
                clientSideOnlyId: generateUuid(),
              },
            ],
            isFko: true,
            returnProfiles: true,
          });

          if (res.profiles?.length) {
            dispatch(setFkoPlayersArray(res.profiles));
          } else {
            dispatch(setFkoPlayersArray([newProfile]));
          }
        } catch (error: unknown) {
          console.error('Error creating MBO clients:', error);
          saveErrorDetails({
            key: 'ftc-registration-landing-createMboClients',
            severity: 'high',
            message: 'Caught error in ProgramsPromotionalLanding.createMboClients',
            description: error instanceof Error ? error.message : String(error),
            context: 'ftc',
            email,
            siteId: formData.location,
            url: window.location.href,
            data: {
              formData,
              error,
            },
          });
          // Fallback to newProfile if MBO client creation fails
          dispatch(setFkoPlayersArray([newProfile]));
          // Re-throw so caller can handle/show error message
          throw new Error('Failed to create client profile. Please try again.');
        }
      }

      if (accountHolder) {
        setCurrentAccountHolderProfileId(accountHolder._id);
      }
    },
    [dispatch]
  );

  const accountVerification = useAccountVerification({
    onVerificationSuccess: async (payload) => {
      try {
        await processUserExistsOrCreateInitialProfile(payload);
        const navigatePath =
          payload.navigatePath ||
          `${payload.siteId}/${store.getState().fkoFormDataReducer.initialParams.trainingProgram ?? ''}`;
        navigate(navigatePath);
      } catch (error: unknown) {
        console.error('Error processing user profile:', error);
        setErrorMessage(error instanceof Error ? error.message : 'Failed to create profile. Please try again.');
        setIsLoading(false);
        throw error;
      }
    },
    getNavigatePath: (payload) => {
      const initialParams = store.getState().fkoFormDataReducer.initialParams;
      return `${payload.siteId}/${initialParams.trainingProgram ?? ''}`;
    },
    getCachedUserExists,
  });

  const onSubmitHandler = useCallback(
    async (data: RegistrationFormTypes) => {
      setIsLoading(true);
      if (!IS_TEST_ENVIRONMENT && import.meta.env.VITE_IS_CAPTCHA_DISABLED !== 'true') {
        try {
          const recaptchaToken = await executeRecaptchaV3(recaptchaSiteKey);
          const recaptchaResult = await verifyRecaptcha(recaptchaToken);
          if (!recaptchaResult.success || recaptchaResult.score < 0.3) {
            setErrorMessage('reCAPTCHA challenge failed');
            return;
          }
        } catch (err) {
          console.error('Error verifying recaptcha:', err);
          setErrorMessage('reCAPTCHA challenge failed');
          setIsLoading(false);
          return;
        }
      }
      const initialParams = store.getState().fkoFormDataReducer.initialParams;
      console.log('initialParams:', initialParams);
      const sanitizedPath = basePathWithoutSessionId.startsWith('/')
        ? basePathWithoutSessionId
        : `/${basePathWithoutSessionId}`;
      const sanitizedFormUrl = typeof window !== 'undefined' ? `${window.location.origin}${sanitizedPath}` : undefined;

      const baseParams = {
        alternateProfileData: data,
        initialParams,
        locationId: data.location,
        fkoMethod,
        formSubmissionUrl: sanitizedFormUrl,
      };

      try {
        await sendFkoHubspotFormData({...baseParams, ipAddress});
      } catch (error) {
        console.error('Error sending HubSpot data:', error);
      }

      fullStoryService.identify(data.email, {
        displayName: `${data.firstName} ${data.lastName}`, // displayName and email are used in FullStory to label data
        email: data.email,
        locationId: data.location,
        locationName: getLocationBySiteId(data.location)?.name,
        firstName: data.firstName,
        lastName: data.lastName,
      });

      const navigatePath = `${data.location}/${initialParams.trainingProgram ?? ''}`;
      try {
        // note: This is being used only to check if a user exists, not for confirming eligibility like FKO.
        // The classes eligibility check is in ProgramsCheckout.
        // First, check if profile and account exists or not
        const statusResult = await getCachedUserExistsStatus({
          email: data.email,
        });

        const needsVerification = await accountVerification.checkAndHandleVerification(
          statusResult,
          data.email,
          data,
          data.location,
          store.getState().fkoFormDataReducer.initialParams.sessionTypeNames
        );
        if (needsVerification) {
          // The hook will handle setting navigatePath via getNavigatePath callback
          return;
        }

        // No verification needed - fetch full data
        const lookupResult = await getCachedUserExists({
          email: data.email,
          siteId: data.location,
          sessionTypeNames: store.getState().fkoFormDataReducer.initialParams.sessionTypeNames,
        });

        await processUserExistsOrCreateInitialProfile({
          lookupResult,
          email: data.email,
          siteId: data.location,
          formData: data,
        });
        navigate(navigatePath);
      } catch (err: unknown) {
        console.error('Error checking existing account:', err);
        saveErrorDetails({
          key: 'ftc-registration-landing-checkUserExists',
          severity: 'high',
          message: 'Caught error in RegistrationLanding.checkUserExists',
          description: err instanceof Error ? err.message : String(err),
          context: 'ftc',
          email: data.email,
          siteId: data.location,
          url: window.location.href,
          data: {
            formData: data,
            error: err,
          },
        });
        setErrorMessage(
          err instanceof Error && err.message ? err.message : 'Error processing your registration. Please try again.'
        );
      } finally {
        if (!accountVerification.pendingPhoneVerification && !accountVerification.pendingEmailVerification) {
          setIsLoading(false);
        }
      }
    },
    [
      accountVerification,
      basePathWithoutSessionId,
      fkoMethod,
      fkoAnalyticsPrefix,
      getCachedUserExists,
      getCachedUserExistsStatus,
      ipAddress,
      processUserExistsOrCreateInitialProfile,
      navigate,
    ]
  );

  let locationIsBlocked = false;
  const {locationId} = store.getState().fkoFormDataReducer.initialParams;
  if (locationId) {
    const location = getLocationBySiteId(locationId);
    if (!location) {
      locationIsBlocked = true;
    } else {
      locationIsBlocked = !isLocationPermitted({locationId, isClasses, isFreeTrialClasses});
    }
  }

  return (
    <>
      <Helmet>
        <title>TOCA Soccer | Classes</title>
      </Helmet>

      {accountVerification.showVerifyChoice && accountVerification.showVerifyChoice.phoneNumber && (
        <VerificationChoiceModal
          email={accountVerification.showVerifyChoice.email}
          phoneNumber={accountVerification.showVerifyChoice.phoneNumber}
          isAccountCreation={accountVerification.showVerifyChoice.isAccountCreation}
          onChoosePhone={accountVerification.handleChoosePhone}
          onChooseEmail={accountVerification.handleChooseEmail}
        />
      )}

      {accountVerification.pendingEmailVerification && (
        <EmailOtpModal
          email={accountVerification.pendingEmailVerification.email}
          code={accountVerification.emailOtpCode}
          onCodeChange={accountVerification.setEmailOtpCode}
          error={accountVerification.emailOtpError}
          isSending={accountVerification.isSendingEmailOtp}
          isLoading={accountVerification.isLoading}
          resendSuccessMessage={accountVerification.resendSuccessMessage}
          resendCooldownSeconds={accountVerification.getResendCooldownSeconds()}
          onVerify={accountVerification.handleEmailVerificationComplete}
          onCancel={accountVerification.handleCancelEmailVerification}
          onResend={accountVerification.handleResendEmailCode}
        />
      )}

      {accountVerification.pendingPhoneVerification && (
        <PhoneVerificationModal
          email={accountVerification.pendingPhoneVerification.formData.email}
          phoneNumber={accountVerification.pendingPhoneVerification.phoneNumber}
          isNewAccount={accountVerification.pendingPhoneVerification.isNewAccount}
          onVerificationComplete={accountVerification.handlePhoneVerificationComplete}
          onSwitchToEmail={accountVerification.handleSwitchPhoneToEmail}
        />
      )}

      <div className="grow flex flex-col gap-3 sm:gap-6">
        <MenuBar showHamburger={false} />

        <div className="flex flex-col items-center my-4">
          <PageHeading text="TOCA Soccer Classes" />
        </div>

        {locationIsBlocked ? (
          <div className="grid place-content-center w-full py-8">
            <div className="max-w-[360px] flex flex-col gap-3 items-center px-8 py-6 bg-white shadow-flat-lg shadow-primary/20 rounded text-center">
              <p className="text-lg text-primary text-center">This location does not yet offer online class booking</p>
              <p className="text-base text-black font-light">Please check back soon!</p>
              <Link to={`/ftc`} className="mt-4">
                <Button text="see other class options" />
              </Link>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-8">
            <div>
              <p className="max-w-[400px] text-base text-primary font-medium text-center">
                Schedule a FREE, no-commitment class for your child.
              </p>
              <p className="max-w-[400px] text-sm text-primary font-medium text-center mt-1">
                Our Classes are designed for casual and rec league players ages 1-13* at participating centers.
              </p>
            </div>

            <div className="relative grow flex flex-col max-w-[600px] py-4 px-8 bg-white border border-primary/20 rounded shadow-flat-lg mb-12">
              {isLoading ? (
                <div className="absolute top-0 left-0 h-full w-full grid place-content-center bg-primary bg-opacity-80 rounded pointer-events-none">
                  <Spinner darkMode />
                </div>
              ) : null}

              <FkoRegistrationLandingForm
                errorMessage={errorMessage}
                onSubmitHandler={onSubmitHandler}
                getCachedUserExists={getCachedUserExists}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
});
ProgramsPromotionalLanding.displayName = 'ProgramsPromotionalLanding';
